import PropTypes from 'prop-types';
import React from 'react';

import { MatchPhase, matchPhaseArray, MatchPhaseRound, nonLinearCompetitionPhasesArray } from '../../../typings/match';
import { colorSchemaList, componentSizeList } from '../../../typings/design-tokens';
import { IComponent } from '../../../typings/generic-component';
import { getFooter, getHeader } from '../../../utils/match-header-footer';
import { addIsLoseOnTeams, computeFullTime } from '../../../utils/match-status';
import { isPropTypeNullable } from '../../../utils/prop-types';
import { uniqueId } from '../../../utils/times'; // FIXME: need better mocked data/interfaces in tests to remove this
import MatchDataRow from '../../atoms/match-data-row/match-data-row';
import MatchTableHead from '../../atoms/match-table-head/match-table-head';
import MatchRow, { IMatchRow } from '../../molecules/match-row/match-row';
import { Source, MatchRowSources, CtaTarget, CtaTargets, RESULTS } from '../../../typings/match-row';
import { IClassNames, makeClassNames } from '../../../utils/styles';

import './results-table.scss';

export interface IMatchDayList {
  date: string;
  matchList: Array<IMatchRow>;
  roundGroup?: string | null; // BE sends NULL if COMPETITION is LINEAR
  roundName?: string | null; // BE sends NULL if COMPETITION is LINEAR
  // 2021-01-26 - Added/changed PROPS according to REAL BE data!
  competitionId?: string | number; // BE sends both STRING or NUMBER in different QUERY payloads!
  round?: number;
  seasonId?: string | number; // BE sends both STRING or NUMBER in different QUERY payloads!
  title?: string;
  // TODO: 2022-02-25: BE will MOVE these PROPS from IMatchRow to IMatchDayList to avoid DUPLICATION in EACH match!
  // Should CHECK if in ALL matchList arrays we ALWAYS have same COMPETITION PHASE/ROUND data!
  competitionPhase?: MatchPhase; // BE sends NULL if COMPETITION is LINEAR
  competitionPhaseRound?: MatchPhaseRound; // Andata/Ritorno
}

export interface IResultsTable extends IComponent {
  matchDayList: Array<IMatchDayList>;
  margin?: boolean;
  showMatchRowLiveResult?: boolean;
  type?: Source; // Needed by MatchRow for conditional rendering
  target?: CtaTarget;
  hasTableHead?: boolean;
  isMobileDateShort?: boolean;
}

const classNames = (margin: boolean): IClassNames =>
  makeClassNames({
    resultsTable: ['results-table', { 'results-table--margin': margin }],
  });

const ResultsTable: React.FC<IResultsTable> = ({
  /* istanbul ignore next: default value on mandatory field */ matchDayList = [],
  /* istanbul ignore next: default value */ showMatchRowLiveResult = true,
  /* istanbul ignore next: default value */ margin = true,
  /* istanbul ignore next: default value */ type = RESULTS,
  colorSchema,
  size,
  trackingData,
  target,
  hasTableHead = true,
  isMobileDateShort = true,
}) => {
  const cn = classNames(margin);

  const hasRoundGroup = matchDayList.length > 0 && matchDayList[0].roundGroup !== null;
  return (
    <table className={cn.resultsTable}>
      {hasTableHead && <MatchTableHead hasRoundGroup={hasRoundGroup} />}
      <tbody>
        {matchDayList.map((matchDay: IMatchDayList) => {
          return (
            <React.Fragment key={`${matchDay.competitionId ?? uniqueId()}-${matchDay.date ?? uniqueId()}`}>
              <MatchDataRow
                key={matchDay.date ?? uniqueId()}
                date={matchDay.date}
                title={matchDay.title}
                colorSchema={colorSchema}
                roundName={matchDay.roundName}
                dateTimeFormatLong="dddd D MMM YYYY"
                dateTimeFormatShort="ddd D MMM"
                isMobileDateShort={isMobileDateShort}
              />
              {matchDay.matchList
                .map(addIsLoseOnTeams)
                // FIXME: LC 2021-02-17 - COMPUTE match until new 'FullTimeAfterShootOut' and 'FullTimeAfterShootOut' statuses will be provided by BE!
                .map(computeFullTime)
                .map((match: IMatchRow) => {
                  const header = getHeader(match);
                  const footer = getFooter(match);
                  return (
                    <MatchRow
                      {...match}
                      key={match.id ?? match.matchId ?? uniqueId()}
                      colorSchema={colorSchema}
                      size={size}
                      showLiveResult={showMatchRowLiveResult}
                      header={header}
                      footer={footer}
                      type={type}
                      trackingData={trackingData}
                      target={target}
                      roundGroup={matchDay.roundGroup}
                      linkArialabel={`Vai alla partita ${match.home.name} - ${match.away.name}`}
                    />
                  );
                })}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  ResultsTable.propTypes = {
    colorSchema: PropTypes.oneOf(colorSchemaList),
    margin: PropTypes.bool,
    matchDayList: PropTypes.arrayOf(
      PropTypes.exact({
        competitionId: isPropTypeNullable(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
        date: PropTypes.string.isRequired,
        matchList: PropTypes.arrayOf(PropTypes.exact(MatchRow.propTypes!).isRequired).isRequired,
        round: isPropTypeNullable(PropTypes.number),
        roundGroup: isPropTypeNullable(PropTypes.string),
        roundName: isPropTypeNullable(PropTypes.string),
        seasonId: isPropTypeNullable(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
        title: isPropTypeNullable(PropTypes.string),
        competitionPhase: PropTypes.oneOf<MatchPhase>(nonLinearCompetitionPhasesArray),
        competitionPhaseRound: PropTypes.oneOf<MatchPhaseRound>(matchPhaseArray),
      }).isRequired
    ).isRequired,
    showMatchRowLiveResult: PropTypes.bool,
    size: PropTypes.oneOf(componentSizeList),
    target: isPropTypeNullable(PropTypes.oneOf<CtaTarget>(CtaTargets)),
    trackingData: PropTypes.object,
    type: PropTypes.oneOf<Source>(MatchRowSources),
  };
}

export default ResultsTable;
